import { Grid, ProfilePhoto, Text } from "@minervaproject/fds";
import { kebabCase } from "lodash";
import { useEffect } from "react";

import { getColumns } from "components/breakout_control_room/breakout_rooms_preview";
import { CenteredErrorBoundary, FlightErrorBoundary } from "components/error_boundary";
import { BreakoutTimeRemaining } from "components/shared/timers";
import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { CapabilityType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { computeInitials, longDisplayName, profilePhotoUrl } from "utils/user";

function BreakoutRoomOverviewItem({ title, users }: { title: string; users: UserType[] }) {
  if (users.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-column"
      data-testid="group-overview-item"
      style={{ overflow: "hidden" }}
    >
      <div className="flex justify-between items-center p2 mb1 bg-black-tint-20">
        <div className="bold text-black-tint-70">{title}</div>
      </div>
      <div data-testid={`group-overview-item-users-${kebabCase(title)}`}>
        {users.map((user) => (
          <div
            key={user.id}
            className="flex items-center my1 cursor-pointer"
            data-testid={`breakout-room-user-${user.id}`}
            style={{ opacity: user.is_present ? 1 : 0.5 }}
          >
            <ProfilePhoto
              className="outline-2-black-tint-10 mr2"
              size="md"
              src={profilePhotoUrl(user)}
              initials={computeInitials(user)}
            />
            <Text kind="body-l" color="white">
              {longDisplayName(user)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// Provides an overview similar to `BreakoutRoomsList`, but designed for use in the presentation view.
function BreakoutRoomsOverview({ breakoutSessionId }: { breakoutSessionId: string }) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const allUsers = Object.values(useUsersProperties(PROFILE_PROPERTIES.concat("is_present")));
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const breakoutSession = useAppSelector(
    classroomSelectors.selectBreakoutSessionById(breakoutSessionId),
  );
  const breakoutId = breakoutSession.breakout_id;
  const breakoutRooms = useArraySelector(
    classroomSelectors.selectBreakoutRoomsByBreakoutId(breakoutId),
  );
  const isBreakoutTimed = !!breakoutSession.ended_on;
  const isSoloBreakout = breakoutRooms.every(
    (breakoutRoom) => breakoutRoom.assigned_user_ids.length === 1,
  );

  useEffect(() => {
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.SHOW_BREAKOUT_OVERVIEW_ROOM,
        userId: currentUserId,
        contextData: { breakoutSessionId },
      }),
    );
  }, [dispatch, currentUserId, breakoutSessionId]);

  return (
    <div
      className="p4 body-l text-white bg-black-tint-10 h-full"
      data-testid="breakout-rooms-overview"
    >
      {isBreakoutTimed && (
        <h2 className="bold text-white px4 mb3 flex justify-end">
          <FlightErrorBoundary text="The remaining time cannot be displayed">
            <BreakoutTimeRemaining breakoutSessionId={breakoutSessionId} />
          </FlightErrorBoundary>
        </h2>
      )}
      {isSoloBreakout ? (
        <CenteredErrorBoundary text="The breakout room overview cannot be displayed">
          <BreakoutRoomOverviewItem
            title="Solo Breakouts"
            users={allUsers.filter(({ id }) => canParticipateInClassUserIds.includes(id))}
          />
        </CenteredErrorBoundary>
      ) : (
        <Grid
          className="px4 h-full"
          columnGap={4}
          rowGap={4}
          columns={getColumns(breakoutRooms.length)}
          autoRows="1fr"
        >
          {breakoutRooms.map((breakoutRoom) => (
            <CenteredErrorBoundary
              key={breakoutRoom.id}
              text="This breakout room overview cannot be displayed"
            >
              <BreakoutRoomOverviewItem
                title={`Breakout Group ${breakoutRoom.title}`}
                users={allUsers.filter(({ id }) => breakoutRoom.assigned_user_ids.includes(id))}
              />
            </CenteredErrorBoundary>
          ))}
        </Grid>
      )}
    </div>
  );
}

export { BreakoutRoomOverviewItem, BreakoutRoomsOverview };
